import { useCallback, useMemo, useState } from "react";
import fetch from "isomorphic-fetch";
import { useAppConfig } from "@ct-react/core";

type ProcessPromise<T, TBody = undefined> = TBody extends undefined
  ? () => Promise<T | undefined>
  : (data: TBody) => Promise<T | undefined>;

type Result<T, TBody = undefined> = [ ProcessPromise<T,  TBody>, { data?: T, loading: boolean, error?: string } ];

export const useLazyLocaleApi = <T, TBody = undefined>(path: string, method: "GET" | "POST" | "PUT" = "GET"): Result<T, TBody> => {

  const { baseUrl } = useAppConfig();

  const url = useMemo(() => `${baseUrl}/local-api${path.charAt(0) === "/" ? path : `/${path}`}`, [ path, baseUrl ]);
  const [ data, setData ] = useState<T | undefined>(undefined);
  const [ loading, setLoading ] = useState(false);
  const [ error, setError ] = useState<string | undefined>(undefined);

  const loader = useCallback(async (): Promise<T | undefined> => {
    setLoading(true);
    return fetch(url)
      .then(res => {
        if (res.status >= 400) throw new Error(`GET failure on locale api route: ${path}`);
        const result = res.json() as T;
        setData(result);
        return result;
      })
      .catch(e => {
        setData(undefined);
        setError(e.message);
        throw new Error(e.message);
      })
      .finally(() => setLoading(false));
  }, [ url ]);

  const updater = useCallback(async (data: TBody): Promise<T | undefined> => {
    const options = {
      method,
      body: JSON.stringify(data),
      headers: new Headers({ "content-type": "application/json" })
    }
    setLoading(true);
    return fetch(url, options)
      .then(res => {
        if (res.status >= 400) throw new Error(`POST failure on locale api route: ${path}`);
        const result = res.json() as T;
        setData(result);
        return result;
      })
      .catch(e => {
        setData(undefined);
        setError(e.message);
        throw new Error(e.message);
      })
      .finally(() => setLoading(false));
  }, [ url ]);


  return [ method === "GET" ? loader : updater, { data, loading, error } ] as any;

}
